import React, { useState, useEffect, useMemo } from 'react';
import { type Event, createEvent, updateEvent, formatCurrency } from '../features/events';
import { uploadImage, getImageUrlById } from '../features/images';
import type { SocialLink } from '../shared/types';
import { socialPlatformMeta } from '../shared/lib';
import ClassDescriptionEditor from './dashboard/ClassDescriptionEditor';
import { RecurrenceEditor } from '../shared/ui';
import type { RecurrenceRule } from '../shared/types';
import { endEventSeries, getEventSeriesForEvent } from '../api/events';

interface EventModalProps {
  show: boolean;
  onHide: () => void;
  onEventSaved: () => void;
  event?: Event | null;
  userId: string;
}

const EventModal: React.FC<EventModalProps> = ({ show, onHide, onEventSaved, event, userId }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    descriptionHtml: '',
    dtDateOfEvent: '',
    dtTimeStartOfEvent: '',
    dtTimeEndOfEvent: '',
    isAPaidEvent: false,
    priceOfEntry: '' as string,
    isPublic: false,
    status: 'Draft' as Event['status'],
    capacity: 0 as number,
    /** true = use external link, false = RSVP through system */
    rsvpViaExternalLink: false,
    externalRsvpUrl: '',
    isRsvpOnly: false,
  });
  const [imageId, setImageId] = useState<string | null>(null);
  const [showSocialLinks, setShowSocialLinks] = useState(false);
  const [socialLinks, setSocialLinks] = useState<SocialLink[]>([]);
  const [openPlatformDropdown, setOpenPlatformDropdown] = useState<number | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceRule, setRecurrenceRule] = useState<RecurrenceRule>({
    frequency: 'Weekly',
    interval: 1,
    byWeekday: ['Mon'],
    ends: { kind: 'never' },
  });
  const [showEndSeries, setShowEndSeries] = useState(false);
  const [seriesEndDate, setSeriesEndDate] = useState('');

  /** Keep in sync with footer / contact settings (includes TikTok, etc.) */
  const eventSocialPlatformOptions = useMemo(
    () =>
      Object.entries(socialPlatformMeta).map(([value, meta]) => ({
        value,
        icon: meta.icon,
        title: value === 'custom' ? 'Web link' : meta.title,
      })),
    []
  );

  const formatDateForInput = (d: string | Date) => new Date(d).toISOString().split('T')[0];
  const formatTimeForInput = (d: string | Date) => {
    // Keep stored clock time as-is (avoid timezone shifting in edit form).
    if (typeof d === 'string') {
      const directTimeMatch = d.match(/^(\d{2}:\d{2})/);
      if (directTimeMatch?.[1]) return directTimeMatch[1];
      const isoTimeMatch = d.match(/T(\d{2}:\d{2})/);
      if (isoTimeMatch?.[1]) return isoTimeMatch[1];
    }

    const x = new Date(d);
    return `${x.getUTCHours().toString().padStart(2, '0')}:${x.getUTCMinutes().toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (event) {
      const hasExternal = !!(event.externalRsvpUrl?.trim());
      setFormData({
        name: event.name,
        description: event.description,
        descriptionHtml: event.descriptionHtml ?? '',
        dtDateOfEvent: formatDateForInput(event.dtDateOfEvent),
        dtTimeStartOfEvent: formatTimeForInput(event.dtTimeStartOfEvent),
        dtTimeEndOfEvent: formatTimeForInput(event.dtTimeEndOfEvent),
        isAPaidEvent: event.isAPaidEvent,
        priceOfEntry: event.priceOfEntry === 0 ? '' : String(event.priceOfEntry),
        isPublic: event.isPublic,
        status: event.status,
        capacity: event.capacity == null || event.capacity <= 1 ? 0 : event.capacity,
        rsvpViaExternalLink: hasExternal,
        externalRsvpUrl: event.externalRsvpUrl ?? '',
        isRsvpOnly: event.isRsvpOnly ?? false,
      });
      setImageId(event.imageId ?? null);
      setImageFile(null);
      setIsRecurring(!!event.eventSeriesId);
      setShowEndSeries(false);
      setSeriesEndDate('');

      if (event.eventSeriesId) {
        getEventSeriesForEvent(event.id)
          .then((series) => {
            if (!series) return;
            setRecurrenceRule(series.recurrence);
            setSeriesEndDate(series.endDate ?? '');
          })
          .catch(() => {
            // ignore; recurrence loading is best-effort
          });
      }
      try {
        const links = event.socialLinksJson ? JSON.parse(event.socialLinksJson) : [];
        setSocialLinks(Array.isArray(links) ? links : []);
        setShowSocialLinks(Array.isArray(links) && links.length > 0);
      } catch {
        setSocialLinks([]);
        setShowSocialLinks(false);
      }
    } else {
      const today = new Date().toISOString().split('T')[0];
      setFormData({
        name: '',
        description: '',
        descriptionHtml: '',
        dtDateOfEvent: today,
        dtTimeStartOfEvent: '19:00',
        dtTimeEndOfEvent: '21:00',
        isAPaidEvent: false,
        priceOfEntry: '',
        isPublic: false,
        status: 'Draft',
        capacity: 0,
        rsvpViaExternalLink: false,
        externalRsvpUrl: '',
        isRsvpOnly: false,
      });
      setImageId(null);
      setImageFile(null);
      setSocialLinks([]);
      setShowSocialLinks(false);
      setIsRecurring(false);
      setShowEndSeries(false);
      setSeriesEndDate('');
    }
    setOpenPlatformDropdown(null);
    setError(null);
  }, [event, show]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev) => {
        if (name === 'isPublic' && prev.status !== 'Published') {
          // Ignore attempts to make non-published events public
          return prev;
        }
        return { ...prev, [name]: checked };
      });
    } else if (name === 'priceOfEntry') {
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else if (name === 'capacity') {
      const parsed = parseInt(value, 10);
      setFormData((prev) => ({ ...prev, capacity: Number.isNaN(parsed) ? 0 : Math.max(0, parsed) }));
    } else if (name === 'externalRsvpUrl') {
      setFormData((prev) => ({ ...prev, externalRsvpUrl: value }));
    } else if (name === 'status') {
      const nextStatus = value as Event['status'];
      setFormData((prev) => ({
        ...prev,
        status: nextStatus,
        // Draft / Cancelled events must not be public
        isPublic: nextStatus === 'Published' ? prev.isPublic : false,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    if (error) setError(null);
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) { setError('Event name is required'); return false; }
    if (!formData.dtDateOfEvent) { setError('Event date is required'); return false; }
    if (!formData.dtTimeStartOfEvent) { setError('Start time is required'); return false; }
    if (!formData.dtTimeEndOfEvent) { setError('End time is required'); return false; }
    const startTime = new Date(`${formData.dtDateOfEvent}T${formData.dtTimeStartOfEvent}`);
    const endTime = new Date(`${formData.dtDateOfEvent}T${formData.dtTimeEndOfEvent}`);
    if (startTime >= endTime) { setError('End time must be after start time'); return false; }
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (new Date(formData.dtDateOfEvent) < today) { setError('Event date cannot be in the past'); return false; }
    if (formData.isAPaidEvent) {
      const price = parseFloat(String(formData.priceOfEntry).trim());
      if (Number.isNaN(price) || price <= 0) {
        setError('Price must be greater than 0 for paid events');
        return false;
      }
    }
    if (typeof formData.capacity !== 'number' || formData.capacity < 0) {
      setError('Capacity must be 0 (no limit) or a positive number');
      return false;
    }
    if (formData.rsvpViaExternalLink && !formData.externalRsvpUrl.trim()) {
      setError('Please enter the external RSVP link (e.g. Google Form URL) when using external link.');
      return false;
    }
    if (formData.rsvpViaExternalLink && formData.externalRsvpUrl.trim()) {
      const url = formData.externalRsvpUrl.trim();
      if (!/^https?:\/\/.+/.test(url)) {
        setError('External RSVP link must be a valid http or https URL.');
        return false;
      }
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(true);
    setError(null);
    try {
      let finalImageId: string | null = imageId;
      if (imageFile) {
        const uploaded = await uploadImage(imageFile);
        finalImageId = uploaded.id;
      }
      const startDateTime = `${formData.dtDateOfEvent}T${formData.dtTimeStartOfEvent}`;
      const endDateTime = `${formData.dtDateOfEvent}T${formData.dtTimeEndOfEvent}`;
      const priceNum = parseFloat(String(formData.priceOfEntry).trim());
      const capacityVal =
        formData.capacity === 0 ? 1 : Math.max(1, formData.capacity);
      const externalUrl = formData.rsvpViaExternalLink ? (formData.externalRsvpUrl.trim() || null) : null;
      let descriptionHtml = formData.descriptionHtml;
      if (!descriptionHtml.trim()) {
        descriptionHtml = formData.descriptionHtml;
      }
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = descriptionHtml;
      const plainDescription = tempDiv.textContent || tempDiv.innerText || '';
      const cleanedRecurrence =
        isRecurring || !!event?.eventSeriesId
          ? {
              frequency: 'Weekly' as const,
              interval: 1,
              byWeekday: (recurrenceRule.byWeekday?.length ? recurrenceRule.byWeekday : ['Mon']) as never,
              ends: { kind: 'never' as const },
            }
          : undefined;
      const payload = {
        name: formData.name.trim(),
        description: plainDescription.trim(),
        descriptionHtml: descriptionHtml.trim() || null,
        dtDateOfEvent: formData.dtDateOfEvent,
        dtTimeStartOfEvent: startDateTime,
        dtTimeEndOfEvent: endDateTime,
        ...(cleanedRecurrence ? { recurrence: cleanedRecurrence } : {}),
        isAPaidEvent: formData.isAPaidEvent,
        priceOfEntry: Number.isNaN(priceNum) ? 0 : priceNum,
        isPublic: formData.isPublic,
        status: formData.status,
        capacity: capacityVal,
        externalRsvpUrl: externalUrl,
        imageId: finalImageId ?? null,
        isRsvpOnly: formData.isRsvpOnly,
        socialLinksJson:
          showSocialLinks && socialLinks.length > 0
            ? JSON.stringify(socialLinks.filter((l) => l.url.trim()))
            : null,
      };
      if (event) {
        await updateEvent(event.id, {
          ...payload,
          ...(event.eventSeriesId ? { updateSeries: true } : {}),
        });
      } else {
        await createEvent(payload);
      }
      onEventSaved();
      handleClose();
    } catch (err: any) {
      console.error('Error saving event:', err);
      const message = err?.message || err?.response?.data?.message || err?.response?.data?.error || 'An error occurred while saving the event';
      setError(typeof message === 'string' ? message : 'An error occurred while saving the event');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '', description: '', descriptionHtml: '', dtDateOfEvent: '', dtTimeStartOfEvent: '', dtTimeEndOfEvent: '',
      isAPaidEvent: false, priceOfEntry: '', isPublic: false, status: 'Draft', capacity: 0, rsvpViaExternalLink: false, externalRsvpUrl: '', isRsvpOnly: false,
    });
    setImageId(null);
    setImageFile(null);
    setSocialLinks([]);
    setShowSocialLinks(false);
    setError(null);
    setIsRecurring(false);
    setShowEndSeries(false);
    onHide();
  };

  const currentImageUrl = imageId ? getImageUrlById(imageId) : null;
  const blobPreviewUrl = useMemo(() => {
    if (!imageFile) return null;
    return URL.createObjectURL(imageFile);
  }, [imageFile]);
  useEffect(() => {
    return () => {
      if (blobPreviewUrl) URL.revokeObjectURL(blobPreviewUrl);
    };
  }, [blobPreviewUrl]);
  const imagePreviewUrl = blobPreviewUrl ?? currentImageUrl;

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-primary text-white border-0">
            <div className="d-flex align-items-center">
              <div className="me-3"><i className="bi bi-calendar-event fs-2"></i></div>
              <div>
                <h5 className="modal-title mb-0">{event ? 'Edit Event' : 'Create New Event'}</h5>
                <small className="opacity-75">BMCS Event Management</small>
              </div>
            </div>
            <button type="button" className="btn-close btn-close-white" onClick={handleClose} disabled={isLoading}></button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body p-4">
              {error && (
                <div className="alert alert-danger" role="alert">
                  <i className="bi bi-exclamation-triangle me-2"></i>{error}
                </div>
              )}
              <div className="row g-3">
                <div className="col-12">
                  {!event && (
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isRecurring"
                        checked={isRecurring}
                        onChange={(e) => setIsRecurring(e.target.checked)}
                      />
                      <label className="form-check-label" htmlFor="isRecurring">
                        <i className="bi bi-arrow-repeat me-2"></i>Recurring event
                      </label>
                      <small className="text-muted d-block">
                        Creates a recurring schedule (no duplicate events are stored).
                      </small>
                    </div>
                  )}

                  {!!event?.eventSeriesId && (
                    <small className="text-muted d-block">
                      This is a recurring event. Updates will apply to the recurring schedule.
                    </small>
                  )}
                </div>

                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isRsvpOnly"
                      name="isRsvpOnly"
                      checked={formData.isRsvpOnly}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          isRsvpOnly: checked,
                          ...(checked ? {} : { rsvpViaExternalLink: false, externalRsvpUrl: '', capacity: 0 }),
                        }));
                      }}
                    />
                    <label className="form-check-label" htmlFor="isRsvpOnly">
                      <i className="bi bi-person-check me-2"></i>RSVP-only event (attendees must RSVP)
                    </label>
                  </div>
                  {formData.isRsvpOnly && (
                    <>
                      <label className="form-label d-block mt-3 mb-1">
                        <i className="bi bi-link-45deg me-2"></i>How should attendees RSVP?
                      </label>
                      <div className="d-flex flex-wrap gap-3 align-items-center">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="rsvpMethod"
                            id="rsvpThroughSystem"
                            checked={!formData.rsvpViaExternalLink}
                            onChange={() => setFormData((prev) => ({ ...prev, rsvpViaExternalLink: false, externalRsvpUrl: prev.rsvpViaExternalLink ? '' : prev.externalRsvpUrl }))}
                          />
                          <label className="form-check-label" htmlFor="rsvpThroughSystem">
                            On this site — collect RSVPs here
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="rsvpMethod"
                            id="rsvpExternalLink"
                            checked={formData.rsvpViaExternalLink}
                            onChange={() => setFormData((prev) => ({ ...prev, rsvpViaExternalLink: true }))}
                          />
                          <label className="form-check-label" htmlFor="rsvpExternalLink">
                            External form — use a link (e.g. Google Form)
                          </label>
                        </div>
                      </div>
                      {formData.rsvpViaExternalLink && (
                        <>
                          <input
                            type="url"
                            className="form-control mt-2"
                            id="externalRsvpUrl"
                            name="externalRsvpUrl"
                            value={formData.externalRsvpUrl}
                            onChange={handleInputChange}
                            placeholder="https://forms.google.com/..."
                          />
                          <small className="text-muted d-block mt-1">
                            The &quot;RSVP&quot; button will open this link in a new tab.
                          </small>
                        </>
                      )}
                    </>
                  )}
                </div>
                <div className="col-12">
                  <div className="form-check mb-2">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="showSocialLinks"
                      checked={showSocialLinks}
                      onChange={(e) => setShowSocialLinks(e.target.checked)}
                    />
                    <label className="form-check-label" htmlFor="showSocialLinks">
                      <i className="bi bi-share me-2"></i>Add social media or web links
                    </label>
                  </div>
                  {showSocialLinks && (
                    <div className="border rounded p-3 bg-light">
                      {(() => {
                        return socialLinks.map((link, idx) => {
                          const current =
                            eventSocialPlatformOptions.find((p) => p.value === link.platform) ??
                            eventSocialPlatformOptions[0];
                          return (
                            <div
                              key={idx}
                              className="d-flex align-items-center gap-2 mb-2 flex-nowrap"
                              style={{ minHeight: 38 }}
                            >
                              <div className="dropdown flex-shrink-0 position-relative">
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary btn-sm dropdown-toggle d-flex align-items-center justify-content-center"
                                  style={{ width: 42 }}
                                  aria-expanded={openPlatformDropdown === idx}
                                  aria-label={`Platform: ${current.title}`}
                                  title={current.title}
                                  onClick={() => setOpenPlatformDropdown((prev) => (prev === idx ? null : idx))}
                                >
                                  <i className={`bi ${current.icon}`} style={{ fontSize: '1.1rem' }} />
                                </button>
                                {openPlatformDropdown === idx && (
                                  <>
                                    <div
                                      className="position-fixed"
                                      style={{ inset: 0, zIndex: 1040 }}
                                      aria-hidden
                                      onClick={() => setOpenPlatformDropdown(null)}
                                    />
                                    <ul
                                      className="dropdown-menu show position-absolute"
                                      style={{ zIndex: 1050, marginTop: 2 }}
                                    >
                                      {eventSocialPlatformOptions.map(({ value, icon, title }) => (
                                        <li key={value}>
                                          <button
                                            type="button"
                                            className="dropdown-item d-flex align-items-center gap-2"
                                            onClick={() => {
                                              setSocialLinks((prev) =>
                                                prev.map((l, i) => (i === idx ? { ...l, platform: value } : l))
                                              );
                                              setOpenPlatformDropdown(null);
                                            }}
                                          >
                                            <i className={`bi ${icon}`} />
                                            <span>{title}</span>
                                          </button>
                                        </li>
                                      ))}
                                    </ul>
                                  </>
                                )}
                              </div>
                              <input
                                type="url"
                                className="form-control form-control-sm"
                                placeholder="https://..."
                                style={{ minWidth: 0, flex: '1 1 180px' }}
                                value={link.url}
                                onChange={(e) =>
                                  setSocialLinks((prev) =>
                                    prev.map((l, i) => (i === idx ? { ...l, url: e.target.value } : l))
                                  )
                                }
                              />
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm flex-shrink-0"
                            onClick={() => setSocialLinks((prev) => prev.filter((_, i) => i !== idx))}
                            aria-label="Remove link"
                          >
                            <i className="bi bi-trash" />
                          </button>
                        </div>
                          );
                        });
                      })()}
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm mt-1"
                        onClick={() => setSocialLinks((prev) => [...prev, { platform: 'facebook', url: '' }])}
                      >
                        <i className="bi bi-plus me-1"></i>Add link
                      </button>
                    </div>
                  )}
                </div>
                <div className="col-12">
                <div className="row g-3">
                  <div className="col-md-6">
                    <label htmlFor="status" className="form-label">
                      <i className="bi bi-flag me-2"></i>Status
                    </label>
                    <select
                      id="status"
                      name="status"
                      className="form-select"
                      value={formData.status}
                      onChange={handleInputChange}
                    >
                      <option value="Draft">Draft</option>
                      <option value="Published">Published</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                    <small className="text-muted">
                      Draft events are not shown publicly; Cancelled events remain for record keeping.
                    </small>
                  </div>
                  {formData.isRsvpOnly && !formData.rsvpViaExternalLink && (
                    <div className="col-md-6">
                      <label htmlFor="capacity" className="form-label">
                        <i className="bi bi-people me-2"></i>Capacity (optional)
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="capacity"
                        name="capacity"
                        value={formData.capacity}
                        onChange={handleInputChange}
                        min={0}
                        placeholder="0 = no limit"
                      />
                    </div>
                  )}
                </div>
              </div>
                <div className="col-12">
                  <label className="form-label"><i className="bi bi-image me-2"></i>Event Image</label>
                  {imagePreviewUrl && (
                    <div className="mb-2">
                      <img
                        src={imagePreviewUrl}
                        alt="Event"
                        style={{ maxWidth: 160, maxHeight: 120, objectFit: 'cover', borderRadius: 8 }}
                      />
                    </div>
                  )}
                  <input
                    type="file"
                    className="form-control"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setImageFile(file);
                    }}
                  />
                  <small className="text-muted">Optional. PNG, JPEG or JPG. Max 10MB.</small>
                  {(imageId || imageFile) && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-danger p-0 mt-1 d-block"
                      onClick={() => { setImageId(null); setImageFile(null); }}
                    >
                      Remove image
                    </button>
                  )}
                </div>
                <div className="col-12">
                  <label htmlFor="name" className="form-label"><i className="bi bi-calendar-event me-2"></i>Event Name *</label>
                  <input type="text" className="form-control" id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Enter event name" required />
                </div>
                <div className="col-12">
                  <label className="form-label"><i className="bi bi-text-paragraph me-2"></i>Description</label>
                  <ClassDescriptionEditor
                    key={event?.id ?? 'new'}
                    html={formData.descriptionHtml}
                    onHtmlChange={(html) => setFormData((prev) => ({ ...prev, descriptionHtml: html }))}
                  />
                </div>

                <div className="col-12">
                  <div className="border rounded p-3">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                      <div className="fw-semibold">
                        <i className="bi bi-calendar-date me-2" />
                        Date & time
                      </div>
                      {(isRecurring || !!event?.eventSeriesId) && (
                        <span className="badge text-bg-light border">
                          <i className="bi bi-arrow-repeat me-1" />
                          Recurring schedule
                        </span>
                      )}
                    </div>

                    <div className="row g-3">
                      <div className="col-md-6">
                        <label htmlFor="dtDateOfEvent" className="form-label">
                          <i className="bi bi-calendar-date me-2"></i>Event Date *
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          id="dtDateOfEvent"
                          name="dtDateOfEvent"
                          value={formData.dtDateOfEvent}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label htmlFor="dtTimeStartOfEvent" className="form-label">
                          <i className="bi bi-clock me-2"></i>Start Time *
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="dtTimeStartOfEvent"
                          name="dtTimeStartOfEvent"
                          value={formData.dtTimeStartOfEvent}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div className="col-md-3">
                        <label htmlFor="dtTimeEndOfEvent" className="form-label">
                          <i className="bi bi-clock-fill me-2"></i>End Time *
                        </label>
                        <input
                          type="time"
                          className="form-control"
                          id="dtTimeEndOfEvent"
                          name="dtTimeEndOfEvent"
                          value={formData.dtTimeEndOfEvent}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    {(isRecurring || !!event?.eventSeriesId) && (
                      <div className="mt-3">
                        <label className="form-label mb-1">
                          <i className="bi bi-arrow-repeat me-2"></i>Recurrence
                        </label>
                        <RecurrenceEditor
                          value={recurrenceRule}
                          onChange={setRecurrenceRule}
                          disabled={isLoading}
                        />

                        {event?.eventSeriesId && (
                          <div className="mt-3">
                            {!showEndSeries ? (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                disabled={isLoading}
                                onClick={() => setShowEndSeries(true)}
                              >
                                <i className="bi bi-stop-circle me-2" aria-hidden />
                                End recurring schedule
                              </button>
                            ) : (
                              <div className="border rounded p-2 bg-white">
                                <div className="row g-2 align-items-end">
                                  <div className="col-12 col-md-6">
                                    <label className="form-label mb-1">End date (inclusive)</label>
                                    <input
                                      type="date"
                                      className="form-control form-control-sm"
                                      value={seriesEndDate}
                                      onChange={(e) => setSeriesEndDate(e.target.value)}
                                      disabled={isLoading}
                                    />
                                  </div>
                                  <div className="col-12 col-md-6 d-flex gap-2">
                                    <button
                                      type="button"
                                      className="btn btn-danger btn-sm"
                                      disabled={isLoading || !seriesEndDate.trim()}
                                      onClick={async () => {
                                        if (!event) return;
                                        setIsLoading(true);
                                        setError(null);
                                        try {
                                          await endEventSeries(event.id, seriesEndDate.trim());
                                          setShowEndSeries(false);
                                          onEventSaved();
                                        } catch (err) {
                                          setError(err instanceof Error ? err.message : 'Failed to end series');
                                        } finally {
                                          setIsLoading(false);
                                        }
                                      }}
                                    >
                                      End
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-outline-secondary btn-sm"
                                      disabled={isLoading}
                                      onClick={() => setShowEndSeries(false)}
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12">
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="isAPaidEvent" name="isAPaidEvent" checked={formData.isAPaidEvent} onChange={handleInputChange} />
                    <label className="form-check-label" htmlFor="isAPaidEvent"><i className="bi bi-currency-exchange me-2"></i>This is a paid event</label>
                  </div>
                </div>
                {formData.isAPaidEvent && (
                  <div className="col-md-6">
                    <label htmlFor="priceOfEntry" className="form-label"><i className="bi bi-tag me-2"></i>Entry Price (R) *</label>
                    <input
                      type="number"
                      className="form-control input-no-spinner"
                      id="priceOfEntry"
                      name="priceOfEntry"
                      value={formData.priceOfEntry === '' ? '' : formData.priceOfEntry}
                      onChange={handleInputChange}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      required={formData.isAPaidEvent}
                    />
                    {formData.priceOfEntry !== '' && !Number.isNaN(parseFloat(String(formData.priceOfEntry))) && parseFloat(String(formData.priceOfEntry)) > 0 && (
                      <small className="text-muted">Display price: {formatCurrency(parseFloat(String(formData.priceOfEntry)))}</small>
                    )}
                  </div>
                )}
                <div className="col-12">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="isPublic"
                      name="isPublic"
                      checked={formData.isPublic}
                      onChange={handleInputChange}
                      disabled={formData.status !== 'Published'}
                    />
                    <label className="form-check-label" htmlFor="isPublic">
                      <i className="bi bi-globe me-2"></i>
                      Make this event public (visible to non-members)
                    </label>
                  </div>
                  <small className="text-muted">
                    Only <strong>Published</strong> events can be public; Draft and Cancelled events are always private.
                  </small>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 p-4">
              <button type="button" className="btn btn-outline-secondary" onClick={handleClose} disabled={isLoading}><i className="bi bi-x-circle me-2"></i>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>{event ? 'Updating...' : 'Creating...'}</> : <><i className="bi bi-check-circle me-2"></i>{event ? 'Update Event' : 'Create Event'}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EventModal;
